import { useState } from "react";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { BookingSystemMessage } from "@/types/messages";
import { format, isValid } from "date-fns";
import { useBookings } from "@/hooks/useBookings";
import { useAuth } from "@/contexts/AuthContext";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface BookingRequestMessageProps {
  message: BookingSystemMessage;
  conversationId: string;
}

export function BookingRequestMessage({ message, conversationId }: BookingRequestMessageProps) {
  const { user } = useAuth();
  const { updateBookingRequest } = useBookings();
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [currentAction, setCurrentAction] = useState(message.content.action);
  const { content } = message;
  const { details, action } = content;

  const isTalent = user?.uid === details.talentId;
  const isPending = currentAction === 'created';

  const handleAccept = async () => {
    if (!details.id) return;
    try {
      setCurrentAction('accepted');
      await updateBookingRequest(details.id, user!.uid, { status: 'accepted' });
    } catch (error) {
      setCurrentAction('created');
      console.error('Error accepting booking:', error);
      // Add error toast here
    }
  };

  const handleReject = async () => {
    if (!details.id) return;
    try {
      setCurrentAction('rejected');
      await updateBookingRequest(details.id, user!.uid, { status: 'rejected' });
      setShowRejectDialog(false);
    } catch (error) {
      setCurrentAction('created');
      console.error('Error rejecting booking:', error);
      // Add error toast here
    }
  };

  const getStatusDisplay = (action: string) => {
    switch (action) {
      case 'created':
        return {
          text: 'Pending Review',
          className: 'text-yellow-400'
        };
      case 'accepted':
        return {
          text: 'Accepted',
          className: 'text-green-400'
        };
      case 'rejected':
        return {
          text: 'Declined',
          className: 'text-red-400'
        };
      default:
        return {
          text: 'Unknown',
          className: 'text-gray-400'
        };
    }
  };

  const status = getStatusDisplay(currentAction);

  const formatEventDate = (dateValue: any): string => {
    try {
      // Handle different date formats
      const date = dateValue instanceof Date 
        ? dateValue 
        : typeof dateValue === 'string'
          ? new Date(dateValue)
          : dateValue?.toDate?.(); // Handle Firestore Timestamp

      if (!date || !isValid(date)) {
        console.error('Invalid date value:', dateValue);
        return 'Date not available';
      }

      return format(date, 'MMMM d, yyyy');
    } catch (error) {
      console.error('Error formatting date:', error, dateValue);
      return 'Date not available';
    }
  };

  return (
    <>
      <Card className={`bg-gray-800 border-gray-700 ${currentAction === 'rejected' ? 'opacity-75' : ''}`}>
        <CardContent className="pt-6">
          <div className="space-y-4">
            <div className="flex justify-between items-start">
              <h3 className="text-lg font-semibold text-white">
                Booking Request: {details.eventName}
              </h3>
              <span className={`text-sm ${status.className}`}>
                {status.text}
              </span>
            </div>
            
            <div className={`space-y-2 text-sm ${currentAction === 'rejected' ? 'text-gray-400' : 'text-gray-300'}`}>
              <p>Date: {formatEventDate(details.eventDate)}</p>
              <p>Location: {details.location}</p>
              <p>Call Time: {details.talentCallTime}</p>
              <p>Show Time: {details.showStartTime}</p>
              {details.notes && <p>Notes: {details.notes}</p>}
              <p>Rate: {details.rateType === 'fixed' ? `$${details.fixedRate}` : 'To be discussed'}</p>
              
              {currentAction === 'rejected' && (
                <p className="mt-4 text-red-400 font-medium">
                  This booking request was declined
                </p>
              )}
            </div>
          </div>
        </CardContent>

        {isPending && isTalent && (
          <CardFooter className="justify-end space-x-2 bg-gray-900/50">
            <Button
              variant="outline"
              onClick={() => setShowRejectDialog(true)}
              className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
            >
              Decline
            </Button>
            <Button
              onClick={handleAccept}
              className="bg-green-600 hover:bg-green-500"
            >
              Accept Booking
            </Button>
          </CardFooter>
        )}
      </Card>

      <AlertDialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reject Booking Request</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to reject this booking request? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleReject} className="bg-red-600 hover:bg-red-500">
              Reject
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
} 